import { useCallback, useEffect, useState, type ReactNode } from "react";
import { CollapsedSection } from "@/components/admin/CollapsedSection";
import {
  listQcMailLists,
  setQcMailAddress,
  setQcMailRecipient,
  type QcMailMember,
  type QcMailPerson,
} from "@/lib/manufacturing/builderAssistantClient";

const fieldClass =
  "w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900";

function personKey(person: QcMailPerson): string {
  return person.kind === "address" || !person.user_id ? `addr:${person.email ?? ""}` : `user:${person.user_id}`;
}

function Picker({
  title,
  people,
  kind,
  onChanged,
}: {
  title: string;
  people: QcMailPerson[];
  kind: "failure" | "ready";
  onChanged: () => Promise<void>;
}) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selected = people.find((person) => personKey(person) === key) ?? null;
  const onNow = people.filter((person) => person.opted_in).map((person) => person.name);

  async function toggle(next: boolean) {
    if (!selected) return;
    setBusy(true);
    setError(null);
    const res = selected.user_id
      ? await setQcMailRecipient(selected.user_id, kind, next)
      : await setQcMailAddress(selected.email ?? "", kind, next);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    await onChanged();
  }

  return (
    <div className="space-y-2">
      <select className={fieldClass} value={key} aria-label={title} onChange={(event) => setKey(event.target.value)}>
        <option value="">Select a recipient</option>
        {people.map((person) => (
          <option key={personKey(person)} value={personKey(person)}>
            {person.name}
            {person.kind === "address" ? "" : person.email ? ` (${person.email})` : ""} — {person.opted_in ? "on" : "off"}
          </option>
        ))}
      </select>
      {selected ? (
        <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-zinc-100">
          <input type="checkbox" checked={selected.opted_in} disabled={busy} onChange={(event) => void toggle(event.target.checked)} />
          Receives these emails
        </label>
      ) : null}
      <p className="text-xs text-gray-600 dark:text-zinc-300">On now: {onNow.length ? onNow.join(", ") : "no one"}</p>
      {error ? <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p> : null}
    </div>
  );
}

function AddRecipient({
  members,
  listedIds,
  domains,
  kind,
  onChanged,
}: {
  members: QcMailMember[];
  listedIds: Set<string>;
  domains: string[];
  kind: "failure" | "ready";
  onChanged: () => Promise<void>;
}) {
  const [userId, setUserId] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const choices = members.filter((member) => !listedIds.has(member.user_id));

  async function addMember() {
    if (!userId) return;
    setBusy(true);
    setError(null);
    const res = await setQcMailRecipient(userId, kind, true);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setUserId("");
    await onChanged();
  }

  async function addEmail() {
    if (!email.trim()) return;
    setBusy(true);
    setError(null);
    const res = await setQcMailAddress(email, kind, true);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setEmail("");
    await onChanged();
  }

  return (
    <div className="space-y-2 border-t border-gray-200 pt-3 dark:border-zinc-700">
      <p className="text-sm font-semibold text-gray-800 dark:text-zinc-100">Add someone else</p>
      <p className="text-xs text-gray-500 dark:text-zinc-400">
        Any person in this organization, or a new address on an approved domain. A group mailbox such as logistics@buybennett.com belongs here when buybennett.com is approved.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <select className={`${fieldClass} min-w-48 flex-1`} value={userId} aria-label="Organization member" onChange={(event) => setUserId(event.target.value)}>
          <option value="">Select an organization member</option>
          {choices.map((member) => (
            <option key={member.user_id} value={member.user_id}>
              {member.name}
              {member.email ? ` (${member.email})` : ""}
            </option>
          ))}
        </select>
        <button type="button" className="rounded-md bg-gray-900 px-3 py-1.5 text-sm text-white disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900" disabled={busy || !userId} onClick={() => void addMember()}>
          Add member
        </button>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <input
          className={`${fieldClass} min-w-48 flex-1`}
          value={email}
          aria-label="New email address"
          placeholder={domains[0] ? `name@${domains[0]}` : "name@your-domain.com"}
          onChange={(event) => setEmail(event.target.value)}
        />
        <button type="button" className="rounded-md bg-gray-900 px-3 py-1.5 text-sm text-white disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900" disabled={busy || !email.trim()} onClick={() => void addEmail()}>
          Add new
        </button>
      </div>
      <p className="text-xs text-gray-500 dark:text-zinc-400">
        {domains.length
          ? `Approved domains: ${domains.join(", ")}. A new address has to use one of these.`
          : "This organization has no approved email domains yet, so a new address cannot be added."}{" "}
        Add a missing domain under Org Administration → Email Events → Approved email domains. The same domain can be approved for more than one organization.
      </p>
      {error ? <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p> : null}
    </div>
  );
}

export function QcMailRecipients({ failureSettings = null }: { failureSettings?: ReactNode }) {
  const [failure, setFailure] = useState<QcMailPerson[]>([]);
  const [ready, setReady] = useState<QcMailPerson[]>([]);
  const [members, setMembers] = useState<QcMailMember[]>([]);
  const [domains, setDomains] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [readyNote, setReadyNote] = useState<string | null>(null);
  const [addressNote, setAddressNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await listQcMailLists();
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setError(null);
    setReadyNote(res.data.ready_error);
    setAddressNote(res.data.address_error);
    setFailure(res.data.failure);
    setReady(res.data.ready);
    setMembers(res.data.members ?? []);
    setDomains(res.data.approved_domains ?? []);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const listedIds = new Set(
    [...failure, ...ready].map((person) => person.user_id).filter((id): id is string => Boolean(id)),
  );

  return (
    <div className="space-y-2">
      {error ? <p className="text-xs text-rose-700 dark:text-rose-300">{error}</p> : null}
      {addressNote ? <p className="text-xs text-rose-700 dark:text-rose-300">{addressNote}</p> : null}
      <CollapsedSection title="QC failure and ready for certification">
        {failureSettings}
        <p className="text-xs text-gray-500 dark:text-zinc-400">Failures and ready-for-final-certification notices.</p>
        <Picker title="QC failure and ready for certification" people={failure} kind="failure" onChanged={load} />
        <AddRecipient members={members} listedIds={listedIds} domains={domains} kind="failure" onChanged={load} />
      </CollapsedSection>
      <CollapsedSection title="Ready for delivery">
        <p className="text-xs text-gray-500 dark:text-zinc-400">Sent when a building passes final QC.</p>
        <Picker title="Ready for delivery" people={ready} kind="ready" onChanged={load} />
        {readyNote ? <p className="text-xs text-rose-700 dark:text-rose-300">{readyNote}</p> : null}
        <AddRecipient members={members} listedIds={listedIds} domains={domains} kind="ready" onChanged={load} />
      </CollapsedSection>
    </div>
  );
}
